import ICompFinalData from '@/models/comp-final/ICompFinalData';
import IWorkFile, { newWorkFile } from '@/models/fact-sheet/IWorkFile';
import IFactSheetProperty, { IBaseFactSheet, newFactSheetProperty } from '@/models/fact-sheet/IFactSheetProperty';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ImageUtils from '@/utils/ImageUtils';
import SymbolDetailsUtil from '@/models/common/SymbolDetailsUtil.';
import WorkFileUtils from '@/models/fact-sheet/WorkFileUtils';
import { FieldNames } from '@/constants/FieldNames';
import { newLookupWithStringsNumbersBooleans } from '@/models/common/ILookup';
import CompFinalPropertyUtils from '@/models/comp-final/CompFinalPropertyUtils';
import IMLSPropertyData from '@/models/mls/IMLSPropertyData';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import MlsPropertyDataUtils from '@/models/mls/MlsPropertyDataUtils';
import utils from '@/utils/QSUtils';
import MlsPropertyItemUtils from '@/models/mls/MlsPropertyItemUtils';

export default {
    /**
     * Convert the given comp final data and MLS property data into a work file containing fact sheets.
     * Does not include the sales histories, add them separately.
     * @see FactSheetManager
     *
     * @param compFinalData
     * @returns
     */
    convert(compFinalData: ICompFinalData, mlsPropertyData: IMLSPropertyData): IWorkFile {
        const workFile = newWorkFile();

        let subjectAddress1AndUnitNo: string | null = null;

        for (const compFinalProperty of compFinalData) {
            if (CompFinalPropertyUtils.isSubject(compFinalProperty)) {
                subjectAddress1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(compFinalProperty.address);
            }

            // QSRC-1271
            if (!compFinalProperty.sourceData || compFinalProperty.sourceData.length === 0) {
                continue;
            }

            const factSheetProperty: IFactSheetProperty = newFactSheetProperty();

            const transferItems = factSheetProperty.transfer;
            for (const compFinalSourceData of compFinalProperty.sourceData) {
                // Prevent replacement of existing in case of duplicates
                if (!utils.isKeyInObject(transferItems, compFinalSourceData.fieldName)) {
                    // if photo is from SE, replace link with b64
                    if (compFinalSourceData.fieldName === FieldNames.CompPhoto && compFinalSourceData.value !== null && compFinalSourceData.value.includes('https://')) {
                        ImageUtils.convertToBase64(compFinalSourceData.value, function (b64img: string) {
                            compFinalSourceData.value = b64img;
                        });
                    }

                    transferItems[compFinalSourceData.fieldName] = compFinalSourceData.value;
                }
            }

            //Source Data
            if (compFinalProperty.sources) {
                const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(compFinalProperty.address);

                for (const compFinalDataSource of compFinalProperty.sources) {
                    if (compFinalDataSource.sourceData) {
                        const symbolDetails = SymbolDetailsUtil.getBySymbol(compFinalDataSource.id);

                        const sourceDatas = newLookupWithStringsNumbersBooleans();
                        const sourceDataKeys = Object.keys(compFinalDataSource.sourceData);
                        for (const sourceDataKey of sourceDataKeys) {
                            // Prevent replacement of existing in case of duplicates
                            if (!utils.isKeyInObject(sourceDatas, sourceDataKey)) {
                                sourceDatas[sourceDataKey] = compFinalDataSource.sourceData[sourceDataKey];
                            }
                        }

                        // When MLS we want to add any "_dataFactSheet" values
                        if (SymbolDetailsUtil.isSourceData(symbolDetails, SourceDataInitials.Mls)) {
                            const mlsPropertyItem = MlsPropertyDataUtils.getByAddress1AndUnitNo(mlsPropertyData, address1AndUnitNo);
                            if (mlsPropertyItem) {
                                const factSheetSourceDatas = MlsPropertyItemUtils.getFactSheetDatas(mlsPropertyItem);

                                Object.assign(sourceDatas, factSheetSourceDatas);
                            }
                        }

                        factSheetProperty[symbolDetails.name as keyof IBaseFactSheet] = sourceDatas;
                    }
                }
            }

            workFile.push(factSheetProperty);
        }

        // Subject property should be first in the array
        if (subjectAddress1AndUnitNo) {
            WorkFileUtils.reorderSubjectFirst(workFile, subjectAddress1AndUnitNo);
        }

        return workFile;
    },
};
